import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import { parseISO } from 'date-fns/parseISO';
import type { ModelRegistry } from 'ember-data/types/registries/model';

type Enquiry = ModelRegistry['enquiry'];

export interface EnquiryRun {
  nights: number;
  ongoing: boolean;
  enquiries: Enquiry[];
}

interface Stay {
  start: number;
  end: number;
  enquiries: Enquiry[];
}

function stayOf(enquiry: Enquiry, first: Date): Stay {
  return {
    start: differenceInCalendarDays(parseISO(enquiry.checkIn as string), first),
    end: differenceInCalendarDays(parseISO(enquiry.checkOut as string), first),
    enquiries: [enquiry],
  };
}

function compareStays(a: Stay, b: Stay): number {
  return a.start - b.start || a.end - b.end;
}

export function enquiryRuns(
  enquiries: Enquiry[],
  dates: Date[],
): (EnquiryRun | undefined)[] {
  const [first] = dates;

  if (!first) {
    return [];
  }

  const stays = enquiries
    .filter((enquiry) => !enquiry.isDateless)
    .map((enquiry) => stayOf(enquiry, first))
    .filter(({ start, end }) => end > 0 && start < dates.length)
    .sort(compareStays);

  const runs: Stay[] = [];

  for (const stay of stays) {
    const last = runs[runs.length - 1];

    if (last && stay.start <= last.end) {
      last.end = Math.max(last.end, stay.end);
      last.enquiries.push(...stay.enquiries);
    } else {
      runs.push(stay);
    }
  }

  const byDay: (EnquiryRun | undefined)[] = dates.map(() => undefined);

  for (const { start, end, enquiries: forRun } of runs) {
    const day = Math.max(start, 0);

    byDay[day] = {
      nights: Math.min(end, dates.length) - day,
      ongoing: start < 0,
      enquiries: forRun,
    };
  }

  return byDay;
}
